import { useMemo } from 'react';

import {
  PrivateKeyRecoveryOptionMethodEnum,
  PrivateKeyRecoveryOptionStatusEnum,
} from '#src/__generated__/globalTypes.ts';
import type { CurrentUser } from '#src/contexts/currentUser/index.ts';
import { useCurrentUserContext } from '#src/contexts/currentUser/index.ts';
import { isApplePrivateRelayEmail } from '#src/lib/email.ts';

export type RecoveryOption = NonNullable<
  CurrentUser['wallet']
>['recoveryOptions'][number];

const RecoveryOptionsOrder = {
  [PrivateKeyRecoveryOptionStatusEnum.ACTIVE]: 1,
  [PrivateKeyRecoveryOptionStatusEnum.PENDING_VALIDATION]: 0,
  [PrivateKeyRecoveryOptionStatusEnum.INACTIVE]: 0,
} as const;

export const useRecoveryOptions = () => {
  const { currentUser } = useCurrentUserContext();

  const { email, wallet } = currentUser || {};
  const { recoveryOptions } = wallet || {};

  const recoveryEmails = useMemo(() => {
    if (recoveryOptions) {
      return recoveryOptions
        .filter(
          option =>
            option.status !== PrivateKeyRecoveryOptionStatusEnum.INACTIVE &&
            option.method === PrivateKeyRecoveryOptionMethodEnum.EMAIL &&
            option.destination !== email
        )
        .sort(
          (a, b) =>
            RecoveryOptionsOrder[b.status] - RecoveryOptionsOrder[a.status]
        );
    }
    return [];
  }, [email, recoveryOptions]);

  const accountEmail = useMemo(() => {
    if (recoveryOptions) {
      return recoveryOptions.find(
        option =>
          option.method === PrivateKeyRecoveryOptionMethodEnum.EMAIL &&
          option.destination === email
      );
    }
    return undefined;
  }, [email, recoveryOptions]);

  const phoneNumber = useMemo(() => {
    if (currentUser?.wallet?.recoveryOptions) {
      return currentUser?.wallet?.recoveryOptions.find(
        option => option.method === PrivateKeyRecoveryOptionMethodEnum.PHONE
      );
    }
    return undefined;
  }, [currentUser?.wallet?.recoveryOptions]);

  const activeRecoveryEmails = useMemo(
    () =>
      recoveryEmails.filter(
        option => option.status === PrivateKeyRecoveryOptionStatusEnum.ACTIVE
      ),
    [recoveryEmails]
  );

  const allActiveRecoveryEmailsArePrivateRelay = useMemo(
    () =>
      activeRecoveryEmails.length > 0 &&
      activeRecoveryEmails.every(option =>
        isApplePrivateRelayEmail(option.destination)
      ),
    [activeRecoveryEmails]
  );

  return {
    recoveryEmails,
    accountEmail,
    phoneNumber,
    hasActiveRecoveryEmail: activeRecoveryEmails.length > 0,
    allActiveRecoveryEmailsArePrivateRelay,
  };
};
